import { api } from '@/lib/api';
import type {
  PaginatedResponse,
  ShipmentSearchResponse,
  ShipmentDetail,
  ShipmentSearchFilters,
  InvoiceSummary,
  InvoiceSearchFilters,
  PCAccount,
  AccountAging,
  Carrier,
  PackageStatus,
  PickupInstruction,
  RatesRequest,
  RatesResponse,
  ConfirmShipmentInput,
  ConfirmedShipment,
  CancelShipmentResult,
  EligiblePickup,
  LabelContext,
  PackagePreset,
  PackagePresetInput,
  QuoteShipment,
} from './types';

const BASE = '/v4/planete-courrier';

// ── Shipments ──

export async function searchShipments(
  filters: ShipmentSearchFilters = {},
): Promise<ShipmentSearchResponse> {
  return api.post<ShipmentSearchResponse>(`${BASE}/shipments`, filters);
}

export async function getShipment(number: string): Promise<ShipmentDetail> {
  return api.get<ShipmentDetail>(`${BASE}/shipments/${number}`);
}

export const TERMINAL_STATUSES = new Set([
  'Delivered', 'Cancelled', 'UnableToDeliver', 'Expired',
  'Refused', 'Lost', 'ReturnedToSender', 'Damaged',
]);

// ── Invoices ──

export async function searchInvoices(
  filters: InvoiceSearchFilters = {},
): Promise<PaginatedResponse<InvoiceSummary>> {
  return api.post<PaginatedResponse<InvoiceSummary>>(`${BASE}/invoices/search`, filters);
}

// ── Accounts ──

export async function getAccounts(): Promise<PCAccount[]> {
  return api.get<PCAccount[]>(`${BASE}/accounts`);
}

export async function getAccountAging(
  accountNumber: string,
  dateAsOf?: string,
): Promise<AccountAging> {
  return api.post<AccountAging>(`${BASE}/accounts/${accountNumber}/aging`, { dateAsOf });
}

// ── Reference Data ──

export async function getCarriers(): Promise<Carrier[]> {
  return api.get<Carrier[]>(`${BASE}/carriers`);
}

export async function getPackageStatuses(): Promise<PackageStatus[]> {
  return api.get<PackageStatus[]>(`${BASE}/package-statuses`);
}

// The carrier's legal pickup locations. carrierCode is REQUIRED — the list is
// per carrier, and an unfiltered lookup comes back empty, which would read as
// "this carrier needs no location".
export async function getPickupInstructions(carrierCode: string): Promise<PickupInstruction[]> {
  return api.get<PickupInstruction[]>(`${BASE}/pickup-instructions`, { carrierCode });
}

// ── Rates / Confirm Shipment (draft-first labels) ──

export async function getRates(req: RatesRequest): Promise<RatesResponse> {
  return api.post<RatesResponse>(`${BASE}/shipmentrequest/rates`, req);
}

export async function confirmShipment(req: ConfirmShipmentInput): Promise<ConfirmedShipment> {
  return api.post<ConfirmedShipment>(`${BASE}/shipmentrequest/confirm`, req);
}

// Pickup requests this draft can ATTACH to (one driver stop for several labels).
// Scoped to the chosen service agreement — re-query when the rate selection changes.
export async function getEligiblePickups(
  draftId: number,
  serviceAgreementId: number,
): Promise<{ pickups: EligiblePickup[] }> {
  return api.post<{ pickups: EligiblePickup[] }>(`${BASE}/shipmentrequest/eligible-pickups`, { draftId, serviceAgreementId });
}

export async function cancelShipment(
  number: string,
  cancellationReason?: string,
): Promise<CancelShipmentResult> {
  return api.put<CancelShipmentResult>(`${BASE}/shipments/${number}/cancel`, { cancellationReason });
}

// URL only — do NOT fetch this through `api`. The label is a binary PDF and
// the web_api_server proxy for this route does a raw fetch()/Buffer passthrough
// (the JSON-only `api` client would corrupt it). Hand the URL to the browser
// instead (window.open/<a href>), mirroring `rma/api.ts` `getRmaLabelUrl`.
// Label sizes are PC's labelsizes codes: '11x8.5' (letter) or '6x4' (thermal).
// Default to letter — office labels print on US Letter paper; the thermal default
// produced 4x6in PDFs that printed half-cut (issue #1411).
export function getShipmentLabelUrl(number: string, labelSizeCode = '11x8.5'): string {
  return `/api/v4/planete-courrier/shipments/${number}/label?labelSizeCode=${encodeURIComponent(labelSizeCode)}`;
}

// List shipping labels recorded against a quote (existing-label warning + list).
export async function getQuoteShipments(quoteId: string): Promise<QuoteShipment[]> {
  return api.get<QuoteShipment[]>(`${BASE}/quotes/${quoteId}/shipments`);
}

export async function getLabelContextFromQuote(quoteId: string): Promise<LabelContext> {
  return api.post<LabelContext>(`${BASE}/label-context/from-quote/${quoteId}`);
}

// ── Package presets ──

export async function getPackagePresets(): Promise<PackagePreset[]> {
  return api.get<PackagePreset[]>(`${BASE}/package-presets`);
}

export async function createPackagePreset(data: PackagePresetInput): Promise<PackagePreset> {
  return api.post<PackagePreset>(`${BASE}/package-presets`, data);
}

export async function updatePackagePreset(
  id: string,
  data: Partial<PackagePresetInput>,
): Promise<PackagePreset> {
  return api.put<PackagePreset>(`${BASE}/package-presets/${id}`, data);
}

export async function deletePackagePreset(id: string): Promise<void> {
  return api.delete(`${BASE}/package-presets/${id}`);
}
